/**
 * Pulse Launcher root component.
 *
 * Flow (Chunk 5 task 5.3 — session guard):
 *
 *   on mount
 *     │
 *     ├── createAuthClient({ mode: "portal", ... })
 *     ├── client.getSession()
 *     │       │
 *     │       ├── null   → client.redirectToPortal(window.location.href)
 *     │       │             (auth portal handles sign-in, then redirects back)
 *     │       │
 *     │       └── present → setSession(snapshot)
 *     │
 *     └── client.onSessionChange(setSession)   // refresh / sign-out
 *
 *   render
 *     │
 *     ├── loading           → <LoadingState />
 *     ├── no session        → null   (redirectToPortal already kicked in)
 *     └── session present   → <LauncherGrid session={session} />
 */

import { useEffect, useMemo, useRef, useState } from "react";
import {
  createAuthClient,
  type AuthClient,
} from "@pulse-business-ai/auth-client";
import type { AuthSessionSnapshot } from "@pulse-business-ai/types";

import { readLauncherEnv } from "./env.js";
import { LauncherGrid } from "./components/LauncherGrid.js";

type AuthState =
  | { status: "loading" }
  | { status: "anonymous" }
  | { status: "authed"; session: AuthSessionSnapshot };

function LoadingState(): JSX.Element {
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        fontFamily: "var(--pulse-font-sans, system-ui, sans-serif)",
        color: "var(--pulse-text-muted, #475569)",
      }}
    >
      Loading Pulse Launcher…
    </div>
  );
}

function currentUrlWithoutHash(): string {
  const url = new URL(window.location.href);
  url.hash = "";
  return url.toString();
}

export default function App(): JSX.Element | null {
  const env = useMemo(readLauncherEnv, []);
  const [auth, setAuth] = useState<AuthState>({ status: "loading" });
  const clientRef = useRef<AuthClient | null>(null);

  if (env.e2eSession) {
    return (
      <LauncherGrid
        session={{
          user: {
            id: "launcher-e2e-user",
            email: "e2e@pulsebusiness.ai",
            roles: [{ id: "admin", label: "Admin", scope: "system" }],
            displayName: "Launcher E2E",
          },
          accessToken: "launcher-e2e-token",
          refreshToken: "launcher-e2e-refresh",
          expiresAt: 0,
          provider: "supabase",
        }}
      />
    );
  }

  // One-time auth-client creation. Memoised so HMR / re-render does not
  // construct a new Supabase client (would lose the in-memory subscription).
  if (!clientRef.current) {
    clientRef.current = createAuthClient({
      mode: "portal",
      supabaseUrl: env.supabaseUrl,
      supabaseAnonKey: env.supabaseAnonKey,
      portalUrl: env.authLoginUrl,
    });
  }

  useEffect(() => {
    const client = clientRef.current!;
    let cancelled = false;

    // If auth portal just redirected us back with tokens in the URL hash
    // (Supabase format: #access_token=...&refresh_token=...&type=login),
    // Supabase's detectSessionInUrl consumes them async — we need to wait
    // for onSessionChange to fire with the decoded session BEFORE we give
    // up and bounce back to the portal. Otherwise we get an infinite loop.
    const hasIncomingTokens =
      typeof window !== "undefined" &&
      /(^|[#&])access_token=/.test(window.location.hash);

    void (async () => {
      try {
        // If tokens present in URL, give Supabase up to 5s to consume them
        // before the first getSession() read.
        if (hasIncomingTokens) {
          for (let i = 0; i < 25; i++) {
            const snap = await client.getSession();
            if (cancelled) return;
            if (snap) {
              setAuth({ status: "authed", session: snap });
              // Clean the hash so a refresh doesn't re-process.
              if (typeof window !== "undefined" && window.history?.replaceState) {
                window.history.replaceState(null, "", window.location.pathname + window.location.search);
              }
              return;
            }
            await new Promise((r) => setTimeout(r, 200));
          }
          // 5s elapsed — fall through to anonymous bounce below.
        }

        const snapshot = await client.getSession();
        if (cancelled) return;
        if (!snapshot) {
          setAuth({ status: "anonymous" });
          client.redirectToPortal(currentUrlWithoutHash());
          return;
        }
        setAuth({ status: "authed", session: snapshot });
      } catch (err) {
        // Surface the failure so a misconfigured build (e.g. missing anon
        // key) is visible instead of presenting as a silent redirect loop.
        // eslint-disable-next-line no-console
        console.error("[pulse-launcher] auth check failed:", err);
        if (cancelled) return;
        setAuth({ status: "anonymous" });
        client.redirectToPortal(currentUrlWithoutHash());
      }
    })();

    const unsubscribe = client.onSessionChange((next) => {
      if (cancelled) return;
      if (next) setAuth({ status: "authed", session: next });
      else setAuth({ status: "anonymous" });
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  if (auth.status === "loading") return <LoadingState />;
  if (auth.status === "anonymous") return null; // redirect in flight

  return (
    <LauncherGrid
      session={auth.session}
      onSignOut={async () => {
        try {
          await clientRef.current?.signOut();
        } finally {
          clientRef.current?.redirectToPortal(window.location.href);
        }
      }}
    />
  );
}
